/**
 * CR-8667 — column sorting for the Supplier Performance table.
 *
 * A header click cycles ascending → descending → the default order (rating,
 * then name). The customer-side table is paginated over every supplier, so
 * its sort goes to the server as `?sort=` (suppliers/views/performance_sort.py)
 * and is applied to the whole result before the page is cut. The supplier-side
 * table ("Your Performance by Customer", SI-16) already holds every row, so it
 * is sorted here with the same rules.
 *
 * The rules match the server's: a column sorts by what its cell shows — a
 * figure the cell renders as 0 when absent sorts as 0, while a value shown as
 * N/A or blank (no rating, no OTIF score, no CO2 captured, no warehouse) goes
 * last in both directions. Ties keep the default order.
 */
import { SupplierProfile } from '../../lib/api';
import { sortRows } from '../../hooks/useColumnSort';
import { ServerColumnSort, nextSort, sortParam, useServerColumnSort } from '../../hooks/useServerColumnSort';
import { selfRowOf } from './selfPerformance';

/**
 * The active column (the row's serialized field name) and its direction.
 * The click cycle and `?sort=` value live in the shared hook (CR-8668 reuses them).
 */
export type PerformanceSort = ServerColumnSort;
export { nextSort, sortParam };

/** A figure the cell renders as 0 when absent. */
const zero = (value: unknown): number => {
  const n = typeof value === 'number' ? value : parseFloat(String(value ?? ''));
  return Number.isFinite(n) ? n : 0;
};
/** A figure the cell renders as N/A when absent — null sorts last. */
const orBlank = (value: number | null | undefined): number | null => (value == null ? null : value);
const text = (value: string | null | undefined): string | null => (value?.trim() ? value.trim().toLowerCase() : null);

const ZERO_FIELDS = [
  'qty_received', 'reject_qty', 'customer_liability_ppm', 'supplier_liability_ppm', 'total_ppm',
  'ncr_count', 'return_count', 'response_score', 'total_value', 'reject_value',
  'customer_cost_of_quality', 'supplier_cost_of_quality', 'total_cost_of_quality',
  'po_line_count', 'otif_count', 'arrears_count', 'qty_diff', 'grn_count', 'ontime_count',
  'late_count', 'early_count',
] as const;

/** How each column of the supplier-side table derives its sort key from a row. */
const SELF_ROW_KEYS: Record<string, (row: SupplierProfile) => unknown> = {
  ...Object.fromEntries(ZERO_FIELDS.map((f) => [f, (row: SupplierProfile) => zero(row[f])])),
  // On the supplier-side page the Customer column is the row's customer.
  customer_scope: (row) => text(selfRowOf(row)?.customer_name),
  supplier_site: (row) => text(selfRowOf(row)?.supplier_site_name),
  company_name: (row) => text(row.company_name),
  warehouse_name: (row) => text(row.warehouse_name),
  combined_score: (row) => orBlank(row.combined_score),
  otif_score_percentage: (row) => orBlank(row.otif_score_percentage),
  // "—" / "n/a" when no CO2 was captured — absent data, not a measured zero.
  carbon_emission: (row) => (row.carbon_emission_asn_count ? zero(row.carbon_emission) : null),
};
const NUMERIC_KEYS = Object.keys(SELF_ROW_KEYS).filter(
  (f) => !['customer_scope', 'supplier_site', 'company_name', 'warehouse_name'].includes(f));

/**
 * Sort the supplier-side rows, which the page holds in full.
 *
 * @param rows - every row after the page's own filters
 * @param sort - the sort in effect, or null to keep the server's order
 * @returns a new, sorted array
 */
export function sortSelfRows(rows: SupplierProfile[], sort: PerformanceSort | null): SupplierProfile[] {
  if (!sort) return rows;
  return sortRows(rows, sort.field, sort.direction, { computed: SELF_ROW_KEYS, numericFields: NUMERIC_KEYS });
}

/**
 * Sort state for the Supplier Performance table.
 *
 * @param onChange - called after every change, e.g. to return to page 1
 * @returns the sort in effect and the header click handler
 */
export function usePerformanceSort(onChange?: () => void) {
  return useServerColumnSort(onChange);
}
